/**
 * Customers (and every other kind of account).
 *
 * An operator opens this module thinking "customers" — vendor owners and
 * console operators are technically in the same table but are managed
 * elsewhere. So the page opens ON customers: the Customers segment is the
 * default view, and the other account types are one tab away rather than mixed
 * into the same 240 rows. The segment owns the `role` filter, which is why
 * there is no separate Role dropdown.
 *
 * The rest of what it gained over the first pass: sortable columns, search as
 * you type, dismissable filter chips, customer-state filters (never booked,
 * dormant, no address) that previously had no expression at all, row selection
 * with bulk block/unblock, per-row actions, column and density control, CSV
 * export of the whole filtered set, and saved views.
 */
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  BadgeCheck,
  Ban,
  CalendarOff,
  CheckCircle2,
  Copy,
  ExternalLink,
  Home,
  MapPin,
  MoonStar,
  MoreHorizontal,
  ShieldCheck,
  Sparkles,
  Store,
  UserX,
  Users as UsersIcon,
} from 'lucide-react';
import {
  bool_param,
  enum_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import { BoolFilter, FilterBar, SearchInput, SelectFilter } from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { SavedViews } from '@/components/filters/saved-views';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { bulk_btn, bulk_btn_danger } from '@/components/data-table/bulk-bar';
import { notify } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ago, date_short, person_name, title_case } from '@/lib/format';
import {
  export_users,
  use_set_user_active_by_id,
  use_user_counts,
  use_user_list,
} from '@/features/users/hooks';
import {
  USER_SORT_FIELDS,
  type UserListFilters,
  type UserListItem,
  type UserRole,
} from '@/features/users/types';

const ROLE_TONE: Record<UserRole, 'neutral' | 'primary' | 'warning'> = {
  USER: 'neutral',
  VENDOR: 'primary',
  ADMIN: 'warning',
};

const ROLE_ICON: Record<UserRole, typeof UsersIcon> = {
  USER: UsersIcon,
  VENDOR: Store,
  ADMIN: ShieldCheck,
};

/**
 * Account segments.
 *
 * `customers` is the default, and deliberately means role=USER rather than
 * "everyone": mixing 135 vendor-owner accounts into the customer list is what
 * made every customer-shaped question hard to ask. `all` stays reachable
 * because reconciling a phone number sometimes means searching every account.
 */
/**
 * "How recently active" as one control.
 *
 * Recency is a single axis, and offering both an "active within" and a
 * "dormant for" select let an operator ask for accounts that were both — a
 * query that always returns nothing and looks like a broken filter.
 */
const ENGAGEMENT_VALUES = ['active_7', 'active_30', 'dormant_60', 'dormant_180', 'never'] as const;
type Engagement = (typeof ENGAGEMENT_VALUES)[number];

const ENGAGEMENT_LABEL: Record<Engagement, string> = {
  active_7: 'Active this week',
  active_30: 'Active this month',
  dormant_60: 'Quiet for 60+ days',
  dormant_180: 'Quiet for 180+ days',
  never: 'Never signed in',
};

const ENGAGEMENT_FILTER: Record<Engagement | 'any', Partial<UserListFilters>> = {
  any: {},
  active_7: { active_within_days: 7 },
  active_30: { active_within_days: 30 },
  dormant_60: { dormant_days: 60 },
  dormant_180: { dormant_days: 180 },
  // "Never" is the far end of dormancy; the API counts null last_login_at as
  // dormant, and 365 is its ceiling.
  never: { dormant_days: 365 },
};

const SEGMENTS = ['customers', 'vendors', 'operators', 'all'] as const;
type Segment = (typeof SEGMENTS)[number];

const SEGMENT_ROLE: Record<Segment, UserRole | undefined> = {
  customers: 'USER',
  vendors: 'VENDOR',
  operators: 'ADMIN',
  all: undefined,
};

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  customers: {
    label: 'Customers',
    title: 'Customers',
    blurb: 'Pet owners who book services and place orders',
  },
  vendors: {
    label: 'Vendor owners',
    title: 'Vendor owners',
    blurb: 'Accounts that own a vendor business — manage the business from Vendors',
  },
  operators: {
    label: 'Operators',
    title: 'Operators',
    blurb: 'Console administrator accounts',
  },
  all: { label: 'All accounts', title: 'All accounts', blurb: 'Every account on the platform' },
};

/**
 * A numeric cell. Zero is dimmed rather than hidden: a column of bold zeros
 * draws the eye to the accounts that have done nothing, which is the opposite
 * of what it should do.
 */
function Count({ value }: { value: number }) {
  return (
    <span className={cn('tabular', value === 0 ? 'text-muted-foreground/60' : 'font-semibold')}>
      {value}
    </span>
  );
}

export function UserListPage() {
  const location = useLocation();
  // The segment defaults to 'customers', so a bare /users is the customer list.
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'customers';
  const role = SEGMENT_ROLE[segment];
  const copy = SEGMENT_COPY[segment];
  const [q, set_q] = use_url_state('q', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [is_active, set_is_active] = use_url_state('is_active', bool_param);
  const [has_vendor, set_has_vendor] = use_url_state('has_vendor_profile', bool_param);
  const [city, set_city] = use_url_state('city', string_param);
  const [phone_verified, set_phone_verified] = use_url_state('phone_verified', bool_param);
  const [has_pets, set_has_pets] = use_url_state('has_pets', bool_param);
  const [has_bookings, set_has_bookings] = use_url_state('has_bookings', bool_param);
  const [has_orders, set_has_orders] = use_url_state('has_orders', bool_param);
  const [has_address, set_has_address] = use_url_state('has_address', bool_param);
  // One control for both directions of "how recently active", because they are
  // mutually exclusive questions and two selects invited setting both.
  const [engagement, set_engagement] = use_url_state('engagement', enum_param(ENGAGEMENT_VALUES));
  const [sort, set_sort] = use_url_state('sort', sort_param(USER_SORT_FIELDS));

  const engagement_filter = ENGAGEMENT_FILTER[engagement || 'any'];

  const filters: UserListFilters = {
    page,
    page_size,
    role,
    q: q.trim() || undefined,
    from: from || undefined,
    to: to || undefined,
    is_active: is_active || undefined,
    has_vendor_profile: has_vendor || undefined,
    city: city.trim() || undefined,
    phone_verified: phone_verified || undefined,
    has_pets: has_pets || undefined,
    has_bookings: has_bookings || undefined,
    has_orders: has_orders || undefined,
    has_address: has_address || undefined,
    ...engagement_filter,
  };

  const { data, isLoading, isFetching, error, refetch } = use_user_list(filters, sort);
  // Counts are scoped to the segment, so the tiles describe the list you are
  // looking at rather than the whole table.
  const counts = use_user_counts({ role });
  const set_active = use_set_user_active_by_id();

  /** Any filter change invalidates the page number — page 7 of a new result set is not where you were. */
  const reset_page = () => set_page(1);

  /**
   * Clears the filters but NOT the segment — "clear all" while looking at
   * Customers should leave you looking at customers, not silently widen the
   * list to every account on the platform.
   */
  function clear_all() {
    set_q('');
    set_from('');
    set_to('');
    set_is_active('');
    set_has_vendor('');
    set_city('');
    set_phone_verified('');
    set_has_pets('');
    set_has_bookings('');
    set_has_orders('');
    set_has_address('');
    set_engagement('');
    reset_page();
  }

  const chips = [
    q.trim() && {
      key: 'q',
      label: 'Search',
      value: q.trim(),
      on_clear: () => {
        set_q('');
        reset_page();
      },
    },
    (from || to) && {
      key: 'date',
      label: 'Joined',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
        reset_page();
      },
    },
    is_active && {
      key: 'is_active',
      label: 'Status',
      value: is_active === 'true' ? 'Active' : 'Blocked',
      advanced: true,
      on_clear: () => {
        set_is_active('');
        reset_page();
      },
    },
    has_vendor && {
      key: 'has_vendor_profile',
      label: 'Vendor account',
      value: has_vendor === 'true' ? 'Yes' : 'No',
      advanced: true,
      on_clear: () => {
        set_has_vendor('');
        reset_page();
      },
    },
    city.trim() && {
      key: 'city',
      label: 'City',
      value: city.trim(),
      advanced: true,
      on_clear: () => {
        set_city('');
        reset_page();
      },
    },
    phone_verified && {
      key: 'phone_verified',
      label: 'Phone',
      value: phone_verified === 'true' ? 'Verified' : 'Unverified',
      advanced: true,
      on_clear: () => {
        set_phone_verified('');
        reset_page();
      },
    },
    has_pets && {
      key: 'has_pets',
      label: 'Pets',
      value: has_pets === 'true' ? 'Has pets' : 'No pets',
      advanced: true,
      on_clear: () => {
        set_has_pets('');
        reset_page();
      },
    },
    has_bookings && {
      key: 'has_bookings',
      label: 'Bookings',
      value: has_bookings === 'true' ? 'Has booked' : 'Never booked',
      advanced: true,
      on_clear: () => {
        set_has_bookings('');
        reset_page();
      },
    },
    has_orders && {
      key: 'has_orders',
      label: 'Orders',
      value: has_orders === 'true' ? 'Has ordered' : 'Never ordered',
      advanced: true,
      on_clear: () => {
        set_has_orders('');
        reset_page();
      },
    },
    has_address && {
      key: 'has_address',
      label: 'Address',
      value: has_address === 'true' ? 'On file' : 'None on file',
      advanced: true,
      on_clear: () => {
        set_has_address('');
        reset_page();
      },
    },
    engagement && {
      key: 'engagement',
      label: 'Activity',
      value: ENGAGEMENT_LABEL[engagement],
      advanced: true,
      on_clear: () => {
        set_engagement('');
        reset_page();
      },
    },
  ];

  const columns = useMemo<ColumnDef<UserListItem, unknown>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (u) => person_name(u),
        meta: {
          label: 'Name',
          sort_key: 'name',
          csv: (u) => person_name(u),
        },
        header: 'Name',
        cell: ({ row }) => {
          const u = row.original;
          const named = Boolean(u.first_name || u.last_name);
          return (
            <div className="flex min-w-0 items-center gap-2.5">
              {/* Real profile photo where the account has one, initials
                  otherwise. The role glyph is the last resort — a phone-only
                  signup has no name to take initials from. */}
              <PersonAvatar
                path={u.avatar_url}
                name={named ? person_name(u) : null}
                seed={u.id}
                size="sm"
                fallback_icon={ROLE_ICON[u.role]}
              />
              <span className="flex min-w-0 flex-col">
                {named ? (
                  <Link
                    to={`/users/${u.id}`}
                    className="min-w-0 truncate font-semibold text-foreground underline-offset-2 hover:text-primary hover:underline"
                    data-testid={`user-row-link-${u.id}`}
                  >
                    {person_name(u)}
                  </Link>
                ) : (
                  /*
                    A nameless account is labelled as such rather than having
                    its phone number promoted into the Name column. The phone
                    is a contact detail, not a name, and printing it as one
                    made every OTP signup look like a data error.
                  */
                  <>
                    <Link
                      to={`/users/${u.id}`}
                      className="min-w-0 truncate italic text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
                      data-testid={`user-row-link-${u.id}`}
                    >
                      No name on file
                    </Link>
                    <span className="truncate text-micro tabular text-muted-foreground/80">
                      {u.phone ?? u.email ?? '—'}
                    </span>
                  </>
                )}
              </span>
            </div>
          );
        },
      },
      {
        id: 'contact',
        accessorFn: (u) => u.email ?? u.phone ?? '',
        meta: {
          label: 'Contact',
          csv: (u) => [u.email, u.phone].filter(Boolean).join(' / '),
        },
        header: 'Contact',
        cell: ({ row }) => {
          const u = row.original;
          const named = Boolean(u.first_name || u.last_name);
          // When the phone already appears under "No name on file", repeating
          // it here is noise; the email is the only new information.
          const lines: { value: string; verified: boolean; title: string }[] = [];
          if (u.phone && named) {
            lines.push({ value: u.phone, verified: u.phone_verified, title: 'Phone' });
          }
          if (u.email) {
            lines.push({ value: u.email, verified: u.email_verified, title: 'Email' });
          }
          if (lines.length === 0) return <span className="text-muted-foreground">—</span>;
          return (
            <div className="flex min-w-0 flex-col">
              {lines.map((line, i) => (
                <span
                  key={line.value}
                  className={cn(
                    'flex min-w-0 items-center gap-1',
                    i === 0 ? 'tabular' : 'text-micro text-muted-foreground',
                  )}
                >
                  <span className="min-w-0 truncate">{line.value}</span>
                  {/* A tick, with a title — colour alone must not be the
                      signal for "this contact detail is confirmed". */}
                  {line.verified && (
                    <BadgeCheck
                      className="h-3 w-3 shrink-0 text-success"
                      aria-label={`${line.title} verified`}
                    />
                  )}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        id: 'location',
        accessorFn: (u) => u.city ?? '',
        meta: {
          label: 'Location',
          csv: (u) => [u.city, u.state].filter(Boolean).join(', '),
        },
        header: 'Location',
        cell: ({ row }) => {
          const u = row.original;
          if (!u.city) return <span className="text-muted-foreground/70">No address</span>;
          return (
            <div className="flex min-w-0 items-center gap-1.5">
              <MapPin aria-hidden="true" className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{u.city}</span>
                {u.state && (
                  <span className="truncate text-micro text-muted-foreground">{u.state}</span>
                )}
              </span>
            </div>
          );
        },
      },
      {
        id: 'role',
        accessorFn: (u) => u.role,
        meta: {
          label: 'Role',
          sort_key: 'role',
          csv: (u) => u.role,
          // Inside a single-role segment every value in this column is
          // identical, so it starts hidden and stays available in the menu.
          hidden_by_default: true,
        },
        header: 'Role',
        cell: ({ row }) => (
          <Badge tone={ROLE_TONE[row.original.role]} dot>
            {title_case(row.original.role)}
          </Badge>
        ),
      },
      {
        id: 'status',
        accessorFn: (u) => u.is_active,
        meta: { label: 'Status', csv: (u) => (u.is_active ? 'Active' : 'Blocked') },
        header: 'Status',
        cell: ({ row }) =>
          row.original.is_active ? (
            <Badge tone="success" dot>
              Active
            </Badge>
          ) : (
            <Badge tone="destructive" dot>
              Blocked
            </Badge>
          ),
      },
      {
        id: 'pet_count',
        accessorFn: (u) => u.pet_count,
        meta: { label: 'Pets', sort_key: 'pet_count', align: 'right', csv: (u) => u.pet_count },
        header: 'Pets',
        cell: ({ row }) => <Count value={row.original.pet_count} />,
      },
      {
        id: 'booking_count',
        accessorFn: (u) => u.booking_count,
        meta: {
          label: 'Bookings',
          sort_key: 'booking_count',
          align: 'right',
          csv: (u) => u.booking_count,
        },
        header: 'Bookings',
        cell: ({ row }) => <Count value={row.original.booking_count} />,
      },
      {
        id: 'order_count',
        accessorFn: (u) => u.order_count,
        meta: {
          label: 'Orders',
          align: 'right',
          csv: (u) => u.order_count,
          // Available, but off by default — nine visible columns is already the
          // most a 1512px screen reads comfortably.
          hidden_by_default: true,
        },
        header: 'Orders',
        cell: ({ row }) => <Count value={row.original.order_count} />,
      },
      {
        id: 'has_vendor_profile',
        accessorFn: (u) => u.has_vendor_profile,
        meta: {
          label: 'Vendor',
          align: 'center',
          csv: (u) => (u.has_vendor_profile ? 'Yes' : 'No'),
          hidden_by_default: true,
        },
        header: 'Vendor',
        cell: ({ row }) =>
          row.original.has_vendor_profile ? (
            <Store aria-label="Has a vendor profile" className="mx-auto h-3.5 w-3.5 text-primary" />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'last_login_at',
        accessorFn: (u) => u.last_login_at ?? '',
        meta: {
          label: 'Last seen',
          sort_key: 'last_login_at',
          csv: (u) => u.last_login_at ?? '',
        },
        header: 'Last seen',
        cell: ({ row }) =>
          row.original.last_login_at ? (
            <span className="text-muted-foreground">{ago(row.original.last_login_at)}</span>
          ) : (
            <span className="text-muted-foreground/70">Never</span>
          ),
      },
      {
        id: 'created_at',
        accessorFn: (u) => u.created_at,
        meta: { label: 'Joined', sort_key: 'created_at', csv: (u) => u.created_at },
        header: 'Joined',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {date_short(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  /** Every tile is also a filter, scoped to the segment currently in view. */
  const seg_link = (extra: string) => `/users?segment=${segment}${extra ? `&${extra}` : ''}`;
  const c = counts.data;

  const metrics: Metric[] = [
    {
      key: 'total',
      label: copy.label,
      value: c?.total?.toLocaleString('en-IN'),
      icon: UsersIcon,
      to: seg_link(''),
      loading: counts.isLoading,
    },
    {
      key: 'new',
      label: 'Joined this month',
      value: c?.joined_this_month?.toLocaleString('en-IN'),
      icon: Sparkles,
      tone: 'primary',
      loading: counts.isLoading,
    },
    {
      key: 'never_booked',
      label: 'Never booked',
      value: c?.never_booked?.toLocaleString('en-IN'),
      hint: 'Signed up, never converted',
      icon: CalendarOff,
      tone: 'warning',
      to: seg_link('has_bookings=false'),
      loading: counts.isLoading,
    },
    {
      key: 'dormant',
      label: 'Quiet 60+ days',
      value: c?.dormant?.toLocaleString('en-IN'),
      icon: MoonStar,
      to: seg_link('engagement=dormant_60'),
      loading: counts.isLoading,
    },
    // For customers, a missing address is what blocks a delivery, so it earns
    // the last tile. For the other segments blocked accounts matter more — a
    // blocked operator is a locked-out colleague.
    segment === 'customers'
      ? {
          key: 'no_address',
          label: 'No address',
          value: c?.no_address?.toLocaleString('en-IN'),
          hint: 'Cannot receive a delivery',
          icon: Home,
          to: seg_link('has_address=false'),
          loading: counts.isLoading,
        }
      : {
          key: 'blocked',
          label: 'Blocked',
          value: c?.blocked?.toLocaleString('en-IN'),
          icon: UserX,
          tone: c && c.blocked > 0 ? 'destructive' : 'default',
          to: seg_link('is_active=false'),
          loading: counts.isLoading,
        },
  ];

  async function set_many_active(ids: string[], next: boolean, clear: () => void) {
    // Sequential rather than Promise.all: these are writes against a
    // rate-limited API, and a partial failure has to be reportable per row.
    const failed: string[] = [];
    for (const id of ids) {
      try {
        await set_active.mutateAsync({ id, is_active: next });
      } catch {
        failed.push(id);
      }
    }
    clear();
    const done = ids.length - failed.length;
    if (failed.length === 0) {
      notify.success(
        `${done} ${done === 1 ? 'account' : 'accounts'} ${next ? 'unblocked' : 'blocked'}`,
      );
    } else {
      notify.warning(`${done} of ${ids.length} updated`, {
        description: `${failed.length} could not be changed — vendor accounts must be suspended from their vendor page.`,
      });
    }
  }

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      actions={<SavedViews module={`users:${segment}`} is_filtered={chips.some(Boolean)} />}
      tabs={
        <PageTabs
          current={segment}
          on_change={(next) => {
            set_segment(next as Segment);
            // Switching segment invalidates the page AND the vendor-profile
            // filter, which only means anything inside the customer segment.
            set_has_vendor('');
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({ value: v, label: SEGMENT_COPY[v].label }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<UserListItem>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(u) => u.id}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        row_href={(u) => `/users/${u.id}`}
        storage_key="users"
        export_name="users"
        on_export_all={() => export_users(filters, sort)}
        caption="Platform user accounts"
        test_id="user-list"
        selectable
        empty={{
          icon: UsersIcon,
          title: chips.some(Boolean)
            ? `No ${copy.label.toLowerCase()} match these filters`
            : `No ${copy.label.toLowerCase()} yet`,
          description: chips.some(Boolean)
            ? 'Try widening the date range or clearing a filter.'
            : 'Accounts appear here as people sign up in the app.',
        }}
        filters={
          <FilterBar
            chips={chips}
            on_clear_all={clear_all}
            advanced={
              <>
                <BoolFilter
                  label="Account status"
                  value={is_active}
                  on_change={(v) => {
                    set_is_active(v);
                    reset_page();
                  }}
                  true_label="Active only"
                  false_label="Blocked only"
                  any_label="Active and blocked"
                  test_id="filter-is-active"
                />
                {/* Meaningless inside the Vendor owners segment, where every
                    row owns one by definition. */}
                {segment !== 'vendors' && (
                  <BoolFilter
                    label="Vendor account"
                    value={has_vendor}
                    on_change={(v) => {
                      set_has_vendor(v);
                      reset_page();
                    }}
                    true_label="Owns a vendor profile"
                    false_label="No vendor profile"
                    test_id="filter-has-vendor"
                  />
                )}
                <SearchInput
                  value={city}
                  on_change={(next) => {
                    set_city(next);
                    reset_page();
                  }}
                  label="City"
                  placeholder="Bengaluru…"
                  test_id="filter-city"
                  className="!flex-none !basis-auto"
                />
                <BoolFilter
                  label="Phone"
                  value={phone_verified}
                  on_change={(v) => {
                    set_phone_verified(v);
                    reset_page();
                  }}
                  true_label="Verified"
                  false_label="Not verified"
                  test_id="filter-phone-verified"
                />
                <BoolFilter
                  label="Pets"
                  value={has_pets}
                  on_change={(v) => {
                    set_has_pets(v);
                    reset_page();
                  }}
                  true_label="Has a pet"
                  false_label="No pets yet"
                  test_id="filter-has-pets"
                />
                <BoolFilter
                  label="Bookings"
                  value={has_bookings}
                  on_change={(v) => {
                    set_has_bookings(v);
                    reset_page();
                  }}
                  true_label="Has booked"
                  false_label="Never booked"
                  test_id="filter-has-bookings"
                />
                <BoolFilter
                  label="Orders"
                  value={has_orders}
                  on_change={(v) => {
                    set_has_orders(v);
                    reset_page();
                  }}
                  true_label="Has ordered"
                  false_label="Never ordered"
                  test_id="filter-has-orders"
                />
                <BoolFilter
                  label="Address"
                  value={has_address}
                  on_change={(v) => {
                    set_has_address(v);
                    reset_page();
                  }}
                  true_label="On file"
                  false_label="None on file"
                  test_id="filter-has-address"
                />
              </>
            }
          >
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Search"
              placeholder="Name, email or phone…"
              test_id="filter-q"
            />
            <SelectFilter
              label="Activity"
              value={engagement}
              on_change={(next) => {
                set_engagement(next as Engagement | '');
                reset_page();
              }}
              options={ENGAGEMENT_VALUES.map((v) => ({ value: v, label: ENGAGEMENT_LABEL[v] }))}
              all_label="Any time"
              test_id="filter-engagement"
              width="12rem"
            />
            <DateRangeFilter
              label="Joined"
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
            />
          </FilterBar>
        }
        row_actions={(u) => (
          <DropdownMenu>
            <DropdownMenuTrigger
              className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground opacity-60 transition-all hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/row:opacity-100"
              aria-label={`Actions for ${person_name(u)}`}
              data-testid={`row-actions-${u.id}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/users/${u.id}`}>
                  <ExternalLink className="h-4 w-4" />
                  Open profile
                </Link>
              </DropdownMenuItem>
              {u.has_vendor_profile && (
                <DropdownMenuItem asChild>
                  <Link to={`/vendors?q=${encodeURIComponent(person_name(u))}`}>
                    <Store className="h-4 w-4" />
                    Find vendor profile
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={() => {
                  void navigator.clipboard.writeText(u.id);
                  notify.success('User ID copied');
                }}
              >
                <Copy className="h-4 w-4" />
                Copy user ID
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {u.is_active ? (
                <DropdownMenuItem
                  tone="destructive"
                  // Vendor blocking is refused by the API with a 422 — surface
                  // that here rather than letting the operator find out from a
                  // failed request.
                  disabled={u.role === 'VENDOR'}
                  onSelect={() => {
                    set_active.mutate(
                      { id: u.id, is_active: false },
                      {
                        onSuccess: () =>
                          notify.success(`${person_name(u)} blocked`, {
                            action: {
                              label: 'Undo',
                              on_click: () => set_active.mutate({ id: u.id, is_active: true }),
                            },
                          }),
                        onError: (err) => notify.error(err, 'Could not block this account.'),
                      },
                    );
                  }}
                >
                  <Ban className="h-4 w-4" />
                  {u.role === 'VENDOR' ? 'Block (use vendor page)' : 'Block account'}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onSelect={() => {
                    set_active.mutate(
                      { id: u.id, is_active: true },
                      {
                        onSuccess: () => notify.success(`${person_name(u)} unblocked`),
                        onError: (err) => notify.error(err, 'Could not unblock this account.'),
                      },
                    );
                  }}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Unblock account
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        bulk_actions={({ ids, rows, clear }) => (
          <>
            <button
              type="button"
              className={bulk_btn}
              disabled={set_active.isPending || rows.every((r) => !r.is_active)}
              onClick={() => void set_many_active(ids, true, clear)}
            >
              <CheckCircle2 />
              Unblock
            </button>
            <button
              type="button"
              className={bulk_btn_danger}
              disabled={set_active.isPending || rows.every((r) => r.role === 'VENDOR')}
              onClick={() => void set_many_active(ids, false, clear)}
            >
              <Ban />
              Block
            </button>
            <Link
              to={`/users?${new URLSearchParams(location.search).toString()}`}
              className={bulk_btn}
              onClick={(e) => {
                e.preventDefault();
                void navigator.clipboard.writeText(ids.join('\n'));
                notify.success(`${ids.length} user IDs copied`);
              }}
            >
              <Copy />
              Copy IDs
            </Link>
          </>
        )}
      />
    </DashboardLayout>
  );
}
